export const SUPER_ADMIN_PERMISSION = '*';

export const MODULE_ROUTE_MAP: Record<string, string> = {
  dashboard: '/admin-dashboard',
  products: '/product-management',
  categories: '/category-management',
  inventory: '/inventory-management',
  stock: '/stock-management',
  pricing: '/pricing-markup',
  inquiries: '/inquiry-management',
  customers: '/customer-management',
  quotations: '/quotation-management',
  employees: '/employee-management',
  attendance: '/employee-management/attendance',
  payroll: '/employee-management/payroll',
  reports: '/admin-reports',
  projects: '/work-projects',
  tasks: '/work-tasks',
  time_tracking: '/my-time',
  timesheets: '/timesheets',
  notifications: '/notifications',
  project_reports: '/time-reports',
  settings: '/admin-settings',
  users: '/admin-settings',
  roles: '/admin-settings',
};

export const ROUTE_MODULE_MAP: Record<string, string> = {
  '/admin-dashboard': 'dashboard',
  '/product-management': 'products',
  '/category-management': 'categories',
  '/inventory-management': 'inventory',
  '/stock-management': 'stock',
  '/pricing-markup': 'pricing',
  '/inquiry-management': 'inquiries',
  '/customer-management': 'customers',
  '/quotation-management': 'quotations',
  '/employee-management/attendance': 'attendance',
  '/employee-management/payroll': 'payroll',
  '/employee-management/reports': 'employees',
  '/employee-management': 'employees',
  '/work-projects': 'projects',
  '/work-tasks': 'tasks',
  '/my-time': 'time_tracking',
  '/timesheets': 'timesheets',
  '/time-reports': 'project_reports',
  '/notifications': 'notifications',
  '/admin-reports': 'reports',
  '/admin-settings': 'settings',
};

export function hasPermission(permissions: string[] | undefined | null, key: string): boolean {
  if (!permissions?.length) return false;
  if (permissions.includes(SUPER_ADMIN_PERMISSION)) return true;
  return permissions.includes(key);
}

/** Whether prices/costs may be shown in a module (requires `{module}.show_price`). */
export function canShowPrice(permissions: string[] | undefined | null, module: string): boolean {
  return hasPermission(permissions, `${module}.show_price`);
}

export function canAccessSettings(permissions: string[] | undefined | null): boolean {
  return (
    hasPermission(permissions, 'settings.view') ||
    hasPermission(permissions, 'users.view') ||
    hasPermission(permissions, 'roles.view') ||
    hasPermission(permissions, SUPER_ADMIN_PERMISSION)
  );
}

export function canAccessHr(permissions: string[] | undefined | null): boolean {
  return (
    hasPermission(permissions, 'employees.view') ||
    hasPermission(permissions, 'attendance.view') ||
    hasPermission(permissions, 'payroll.view') ||
    hasPermission(permissions, SUPER_ADMIN_PERMISSION)
  );
}

export function isPathAllowed(permissions: string[] | undefined | null, pathname: string): boolean {
  if (hasPermission(permissions, SUPER_ADMIN_PERMISSION)) return true;

  if (pathname.startsWith('/admin-settings')) {
    return canAccessSettings(permissions);
  }

  if (pathname.startsWith('/employee-management/reports')) {
    return canAccessHr(permissions);
  }

  const match = Object.entries(ROUTE_MODULE_MAP)
    .sort(([a], [b]) => b.length - a.length)
    .find(([route]) => pathname === route || pathname.startsWith(`${route}/`));
  if (!match) return false;
  const routeModule = match[1];
  return hasPermission(permissions, `${routeModule}.view`);
}

export function firstAllowedPath(permissions: string[] | undefined | null): string {
  const order = [
    'dashboard',
    'products',
    'categories',
    'stock',
    'inventory',
    'pricing',
    'inquiries',
    'customers',
    'quotations',
    'projects',
    'tasks',
    'time_tracking',
    'timesheets',
    'project_reports',
    'employees',
    'attendance',
    'payroll',
    'reports',
    'settings',
  ];
  for (const routeModule of order) {
    if (routeModule === 'settings') {
      if (canAccessSettings(permissions)) return '/admin-settings';
      continue;
    }
    if (hasPermission(permissions, `${routeModule}.view`)) {
      return MODULE_ROUTE_MAP[routeModule];
    }
  }
  return '/admin-login';
}

export const PERMISSION_MODULES = [
  { key: 'dashboard', label: 'Dashboard', actions: ['view', 'show_price'] },
  {
    key: 'products',
    label: 'Products',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  {
    key: 'categories',
    label: 'Categories',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  {
    key: 'stock',
    label: 'Stock Management',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  {
    key: 'pricing',
    label: 'Pricing & Markup',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  { key: 'inquiries', label: 'Inquiries', actions: ['view', 'edit', 'show_price'] },
  {
    key: 'customers',
    label: 'Customers',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  {
    key: 'quotations',
    label: 'Quotations',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  {
    key: 'employees',
    label: 'Employees',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  {
    key: 'attendance',
    label: 'Attendance',
    actions: ['view', 'create', 'edit', 'delete', 'show_price'],
  },
  { key: 'payroll', label: 'Payroll', actions: ['view', 'create', 'edit', 'delete', 'show_price'] },
  { key: 'reports', label: 'Reports', actions: ['view', 'show_price'] },
  { key: 'projects', label: 'Projects', actions: ['view', 'create', 'edit', 'delete'] },
  { key: 'tasks', label: 'Tasks', actions: ['view', 'create', 'edit', 'delete'] },
  { key: 'time_tracking', label: 'Time Tracking', actions: ['view', 'create', 'edit', 'delete'] },
  {
    key: 'timesheets',
    label: 'Timesheets',
    actions: ['view', 'create', 'edit', 'approve', 'reject', 'export'],
  },
  { key: 'notifications', label: 'Notifications', actions: ['view', 'edit'] },
  { key: 'project_reports', label: 'Time Reports', actions: ['view', 'export'] },
  { key: 'settings', label: 'Settings', actions: ['view', 'edit', 'show_price'] },
  { key: 'users', label: 'Users', actions: ['view', 'create', 'edit', 'delete', 'show_price'] },
  { key: 'roles', label: 'Roles', actions: ['view', 'create', 'edit', 'delete', 'show_price'] },
] as const;

export const PERMISSION_TABLE_COLUMNS = [
  'view',
  'create',
  'edit',
  'delete',
  'show_price',
  'approve',
  'reject',
  'export',
] as const;
